import "./components/QuickSearchMount.jsx";
import React, { useState, useEffect } from "react";
import Navbar from "./components/Navbar.jsx";
import LandingHome from "./components/LandingHome.jsx";
import Watchlist from "./components/Watchlist.jsx";
import Feed from "./components/Feed.jsx";
import Companies from "./components/Companies.jsx";
import Screens from "./components/Screens.jsx";
import StockAlerts from "./components/StockAlerts.jsx";
import CompareCompanies from "./components/CompareCompanies.jsx";
import PortfolioTracker from "./components/PortfolioTracker.jsx";
import LoginModal from "./components/LoginModal.jsx";
import SignupModal from "./components/SignupModal.jsx";
import CompanyPage from "./components/company/CompanyPage.jsx";
import { useSummariesVersion } from "./utils/priceData.js";
import { parseHash, clearStockHash } from "./utils/route.js";

import { SESSION_KEY, getSession, saveSession, clearSession } from "./utils/session.js";
import { hydrateUserData, clearUserData } from "./utils/userStore.js";
import { signOut } from "./utils/accounts.js";
import { track } from "./utils/track.js";

const MEMBER_ONLY = ["watchlist", "screens", "alerts", "compare", "portfolio"];

const TITLES = {
  screens: "Screens",
  alerts: "Stock Alerts",
  compare: "Compare Companies",
  portfolio: "Portfolio Tracker",
};

function ComingSoon({ title }) {
  return (
    <main className="page-bg">
      <div className="dash-main-content">
        <h2 className="fw-bold mb-2">{title}</h2>
        <p className="text-muted">This section is coming soon.</p>
      </div>
    </main>
  );
}

export default function App() {
  useSummariesVersion();

  const [currentUser, setCurrentUser] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [page, setPage] = useState("home");
  const [pendingPage, setPendingPage] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [route, setRoute] = useState(() => parseHash());
  useEffect(() => {
    if (route && currentUser) track("view_stock", { ticker: route.ticker, tab: route.tab || "overview" });
  }, [route && route.ticker, route && route.tab, currentUser && currentUser.id]);

  useEffect(() => {
    const onHash = () => {
      setRoute(parseHash());
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const session = getSession();
      if (session) {
        const ok = await hydrateUserData(session.user);
        if (!alive) return;
        if (ok) setCurrentUser(session.user);
        else clearSession();
      }
      if (alive) setCheckingSession(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      if (!getSession()) {
        clearUserData();
        setCurrentUser(null);
        setPage("home");
      }
    }, 30000);

    const onStorage = (e) => {
      if (e.key === SESSION_KEY && !e.newValue) {
        clearUserData();
        setCurrentUser(null);
        setPage("home");
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", onStorage);
    };
  }, [currentUser]);

  const handleNavigate = (key) => {
    track("open_page", { page: key });
    clearStockHash();
    setRoute(null);
    if (MEMBER_ONLY.includes(key) && !currentUser) {
      setPendingPage(key);
      setShowLogin(true);
      return;
    }
    setPage(key);
  };

  const closeModals = () => {
    setShowLogin(false);
    setShowSignup(false);
    setPendingPage(null);
  };

  const handleAuthSuccess = async (userData) => {
    saveSession(userData);
    await hydrateUserData(userData);
    setCurrentUser(userData);
    setShowLogin(false);
    setShowSignup(false);
    setPage(pendingPage || "home");
    setPendingPage(null);
  };

  const handleLogout = async () => {
    await signOut();
    clearSession();
    clearUserData();
    setCurrentUser(null);
    setPage("home");
  };

  if (checkingSession) {
    return (
      <div className="auth-loading">
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  const activePage = !currentUser && MEMBER_ONLY.includes(page) ? "home" : page;

  let content;
  if (route) {
    content = (
      <CompanyPage
        key={route.ticker}
        ticker={route.ticker}
        tab={route.tab}
        user={currentUser}
        onRequireLogin={() => setShowLogin(true)}
      />
    );
  } else if (activePage === "home") content = <LandingHome />;
  else if (activePage === "feed") content = <Feed user={currentUser} />;
  else if (activePage === "companies") content = <Companies user={currentUser} />;
  else if (activePage === "screens") content = <Screens key={currentUser.id} user={currentUser} />;
  else if (activePage === "alerts") content = <StockAlerts key={currentUser.id} user={currentUser} />;
  else if (activePage === "compare") content = <CompareCompanies />;
  else if (activePage === "portfolio") content = <PortfolioTracker key={currentUser.id} user={currentUser} />;
  else if (activePage === "watchlist") content = <Watchlist key={currentUser.id} user={currentUser} onBack={() => setPage("home")} />;
  else content = <ComingSoon title={TITLES[activePage] || "Page"} />;

  return (
    <div className="home-container">
      <Navbar
        user={currentUser}
        page={route ? "" : activePage}
        onNavigate={handleNavigate}
        onOpenLogin={() => setShowLogin(true)}
        onOpenSignup={() => setShowSignup(true)}
        onLogout={handleLogout}
      />

      {content}

      {showLogin && (
        <LoginModal onLogin={handleAuthSuccess} onClose={closeModals} />
      )}
      {showSignup && (
        <SignupModal onSignup={handleAuthSuccess} onClose={closeModals} />
      )}
    </div>
  );
}






